// Collecting the customer's content for a USB or disc duplication order
// (ADR-021): the files a folder pick, a multi-file pick, or a drag-and-drop
// yields, each with the relative path it will be copied to on every unit.

import {
  MAX_MEDIA_CONTENT_FILES,
  mediaPathCollisionKey,
  normalizeMediaRelativePath,
  type MediaPathRejection,
} from 'shared';

export type PickedFile = {
  file: File;
  relative_path: string;
};

// A folder pick reports each file's path from the picked folder down, which is
// the tree the customer wants on the drive. A plain multi-file pick has none, so
// the file sits at the root.
export function pickedFromFileList(list: FileList | null): PickedFile[] {
  if (!list) return [];
  return Array.from(list).map((file) => ({
    file,
    relative_path: file.webkitRelativePath || file.name,
  }));
}

function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const out: FileSystemEntry[] = [];
    const next = (): void => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(out);
          return;
        }
        out.push(...batch);
        next();
      }, reject);
    };
    next();
  });
}

function entryFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

// Thrown when a dropped tree holds more files than a session may carry, so the
// walk stops early instead of enumerating a whole disk.
export class TooManyFilesError extends Error {
  constructor() {
    super('too_many_files');
  }
}

// A drop has to read its entries synchronously, inside the event: the
// DataTransfer is emptied once the handler returns. Call this from the drop
// handler and await the result afterwards.
export function pickedFromDataTransfer(dt: DataTransfer): Promise<PickedFile[]> {
  const entries: FileSystemEntry[] = [];
  for (const item of Array.from(dt.items)) {
    if (item.kind !== 'file') continue;
    const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
    if (entry) entries.push(entry);
  }
  if (entries.length === 0) return Promise.resolve(pickedFromFileList(dt.files));

  return (async () => {
    const out: PickedFile[] = [];
    const walk = async (entry: FileSystemEntry): Promise<void> => {
      if (out.length > MAX_MEDIA_CONTENT_FILES) throw new TooManyFilesError();
      if (entry.isFile) {
        const file = await entryFile(entry as FileSystemFileEntry);
        out.push({ file, relative_path: entry.fullPath.replace(/^\/+/, '') || file.name });
        return;
      }
      if (entry.isDirectory) {
        const children = await readAllEntries((entry as FileSystemDirectoryEntry).createReader());
        for (const child of children) await walk(child);
      }
    };
    for (const entry of entries) await walk(entry);
    return out;
  })();
}

export type SelectionSummary = {
  file_count: number;
  total_bytes: number;
  largest_file_bytes: number;
  top_folders: string[];
  loose_file_count: number;
  sample_paths: string[];
};

export const SUMMARY_SAMPLE_SIZE = 20;

export function summarizeSelection(files: readonly PickedFile[]): SelectionSummary {
  let total = 0;
  let largest = 0;
  let loose = 0;
  const folders = new Set<string>();
  for (const f of files) {
    total += f.file.size;
    if (f.file.size > largest) largest = f.file.size;
    const slash = f.relative_path.indexOf('/');
    if (slash > 0) folders.add(f.relative_path.slice(0, slash));
    else loose += 1;
  }
  const sorted = files.map((f) => f.relative_path).sort((a, b) => a.localeCompare(b));
  return {
    file_count: files.length,
    total_bytes: total,
    largest_file_bytes: largest,
    top_folders: Array.from(folders).sort((a, b) => a.localeCompare(b)),
    loose_file_count: loose,
    sample_paths: sorted.slice(0, SUMMARY_SAMPLE_SIZE),
  };
}

// Two paths that differ only by letter case land on the same file on a FAT or
// exFAT drive, and the session refuses them; name the first pair up front.
export function findCaseCollision(files: readonly PickedFile[]): [string, string] | null {
  const seen = new Map<string, string>();
  for (const f of files) {
    const key = mediaPathCollisionKey(f.relative_path);
    const prior = seen.get(key);
    if (prior !== undefined) return [prior, f.relative_path];
    seen.set(key, f.relative_path);
  }
  return null;
}

// macOS stores a folder's custom icon in a hidden file named "Icon\r". The
// carriage return makes it unstorable, and it is Finder metadata, not content.
function isFolderIconMetadata(relative_path: string): boolean {
  return /(^|\/)Icon\r$/.test(relative_path);
}

// Every path through the shared normalizer the session-create route applies, so
// the path the browser uploads to is byte-for-byte the one the server recorded.
export function normalizeSelection(files: readonly PickedFile[]): {
  files: PickedFile[];
  rejected: { path: string; reason: MediaPathRejection } | null;
} {
  const out: PickedFile[] = [];
  for (const f of files) {
    if (isFolderIconMetadata(f.relative_path)) continue;
    const result = normalizeMediaRelativePath(f.relative_path);
    if (!result.ok)
      return { files: [], rejected: { path: f.relative_path, reason: result.reason } };
    out.push({ file: f.file, relative_path: result.path });
  }
  return { files: out, rejected: null };
}

export function fileMatchKey(
  relative_path: string,
  size: number,
  last_modified: number | null
): string {
  return `${relative_path}\u0000${size}\u0000${last_modified ?? ''}`;
}

// Which upload a line was in the middle of, so a reload can say what to
// re-select. Holds no file bytes and no SAS.
export type MediaContentResumeRecord = {
  session_id: string;
  quote_line_id: string;
  label: string;
  file_count: number;
};

const RESUME_KEY_PREFIX = 'pp.media-content.resume.';

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readResumeRecord(quote_line_id: string): MediaContentResumeRecord | null {
  try {
    const raw = storage()?.getItem(RESUME_KEY_PREFIX + quote_line_id);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MediaContentResumeRecord>;
    if (
      typeof parsed.session_id !== 'string' ||
      parsed.quote_line_id !== quote_line_id ||
      typeof parsed.label !== 'string' ||
      typeof parsed.file_count !== 'number'
    ) {
      return null;
    }
    return parsed as MediaContentResumeRecord;
  } catch {
    return null;
  }
}

export function writeResumeRecord(record: MediaContentResumeRecord): void {
  try {
    storage()?.setItem(RESUME_KEY_PREFIX + record.quote_line_id, JSON.stringify(record));
  } catch {
    // Quota or disabled storage: the server's session state still drives resume.
  }
}

export function clearResumeRecord(quote_line_id: string): void {
  try {
    storage()?.removeItem(RESUME_KEY_PREFIX + quote_line_id);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

// What to call a selection when asking the customer to pick it again: its one
// top-level folder when there is exactly one, else nothing.
export function selectionLabel(summary: SelectionSummary): string {
  return summary.top_folders.length === 1 && summary.loose_file_count === 0
    ? summary.top_folders[0]!
    : '';
}

// A tab running an upload stamps its session here every few seconds, so another
// tab on the same quote can tell a live upload from a paused one and does not
// offer to re-pick or cancel it.
const ACTIVE_KEY_PREFIX = 'pp.media-content.active.';

export function writeActiveHeartbeat(session_id: string): void {
  try {
    storage()?.setItem(ACTIVE_KEY_PREFIX + session_id, String(Date.now()));
  } catch {
    // Without storage a second tab simply sees the session as paused.
  }
}

export function clearActiveHeartbeat(session_id: string): void {
  try {
    storage()?.removeItem(ACTIVE_KEY_PREFIX + session_id);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

export function readActiveHeartbeat(session_id: string): number | null {
  try {
    const raw = storage()?.getItem(ACTIVE_KEY_PREFIX + session_id);
    const at = raw ? Number(raw) : NaN;
    return Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
}
